import { CreatorFanServiceWidget } from '@marpple/mco-membership-ui';
import { HeaderCreatorShop } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/HeaderCreatorShop.ts';
import { CreatorShopSubHeader } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShop/views/CreatorShopSubHeader.ts';
import { applySubHeaderHeadroom } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import { Menu } from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/header';
import { html, type Html, ListView, Page, View } from 'rune-ts';
import sanitizer from 'sanitizer';
import { ConfigSentryF } from '../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { MShopUtilF } from '../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { CreatorConstantS, CreatorTypeS } from '../../../features/Creator/outbound/share';
import { FanLetterData } from '../../../features/FanLetter/type';
import { MEMBERSHIP_UI_IDENTIFIER } from '../../../features/Membership/constant';
import { MembershipErrorEnable } from '../../../features/Membership/error/error_enable';
import { MembershipTypeS, MembershipUtilS } from '../../../features/Membership/outbound/share';
import { getActivatedSdkWithEvent } from '../../../features/Membership/util';
import { PhotoReviewCardType } from '../../../features/PhotoReview/type';
import { ProductCardViewData, ProductCardSize } from '../../../features/ProductList/type';
import { makeEmptyScreenNavigate } from '../../../shared/app/navigate';
import { navigateTo } from '../../../shared/app/util';
import { ButtonAction } from '../../../shared/components/atoms/ButtonAction/ButtonAction';
import { ClientPopupProps, Popup } from '../../../shared/components/cells/Popup/Popup';
import { doIf, isAllCrewById } from '../../../shared/util';
import { getResizedUrl } from '../../../shared/util/image';
import { getSharedData } from '../../../shared/util/rune';
import { makePlusUrl } from '../../../shared/util/url';
import { CreatorMainProfileView } from '../../components/cells/CreatorMainProfile/CreatorMainProfileView';
import { FanLetterItemController } from '../../components/cells/FanLetterItem/controller/FanLetterItemController';
import { FanLetterFormController } from '../../components/cells/FanLetterList/controller/FanLetterFormController';
import { FanLetterListView } from '../../components/cells/FanLetterList/FanLetterListView';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { PhotoReviewCard } from '../../components/cells/PhotoReviewCard/PhotoReviewCard';
import { ProductCardListView } from '../../components/cells/ProductCardList/ProductCardListView.ts';
import { CreatorShopProductListEmptyScreen } from '../../components/cells/ProductCardList/ProductCardListEmptyScreen';
import { SeeMoreLinkPlugin } from '../../components/cells/SectionTitle/plugins';
import { SectionTitle } from '../../components/cells/SectionTitle/SectionTitle';
import klass from './StoreMain.module.scss';
import { type LocalsStoreMembership } from '@services/MarppleShop/shared/type/locals.ts';
import { BecomeFanButtonEvent } from '@services/MarppleShop/renderApp/components/cells/CreatorMainProfile/event/BecomeFanButtonEvent.ts';
import { BecomeFanButtonController } from '@services/MarppleShop/renderApp/components/cells/CreatorMainProfile/controllers/BecomeFanController.ts';
import { FloatingButton_recent_product } from '@services/MarppleShop/renderApp/components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product.ts';
import { FundingListResponse } from '@services/MarppleShop/features/Funding';
import { pipe, map, toArray, take } from '@fxts/core';
import { FundingCard } from '@services/MarppleShop/renderApp/components/cells/FundingCard/FundingCard.ts';
import { FundingHelper } from '@services/MarppleShop/renderApp/components/cells/FundingCard/FundingHelper.ts';

type Membership = LocalsStoreMembership & {
  memberships?: MembershipTypeS.MembershipSummary[];
  my: {
    token?: string;
    memberships?: MembershipTypeS.MembershipSummary[];
  };
};

export type StoreMainPageData = {
  membership?: Membership;
  fundings: FundingListResponse | null;
  creator: CreatorTypeS.CreatorMainProfile;
  products: ProductCardViewData[];
  photo_reviews: PhotoReviewCardType[];
  menus: Menu[];
  layout_options: BaseLayoutOptions;
  adult_check_needed?: boolean;
  is_seller_studio?: boolean;
  popup: ClientPopupProps;
  fan_letters: FanLetterData[];
  is_login: boolean;
  recent_product?: ProductCardViewData;
  server_timestamp: number;
};

class PhotoReviewList extends ListView<PhotoReviewCard> {
  override className = klass.photo_review_list;
  ItemView = PhotoReviewCard;
}

class MembershipView extends View<{
  creator: CreatorTypeS.CreatorMainProfile;
  membership?: Membership;
  layout_options: BaseLayoutOptions;
  title?: SectionTitle;
}> {
  private _membershipErrorEnable = new MembershipErrorEnable(this);

  protected override template(): Html {
    const { membership_plan_existed, membership_commission_existed, memberships } =
      this.data.membership ?? {};

    return html`<div
      class="${MEMBERSHIP_UI_IDENTIFIER} ${klass.membership}"
      data-memberships="${memberships?.length ?? 0}"
      data-is-only-one-existed="${(membership_plan_existed && !membership_commission_existed) ||
      (!membership_plan_existed && membership_commission_existed)}"
      data-is-all-existed="${membership_plan_existed && membership_commission_existed}"
      data-existed="${membership_plan_existed || membership_commission_existed}"
    >
      <div class="${klass.membership_wrap}">
        <div class="${klass.title}">${this.data.title ?? ''}</div>
      </div>
    </div>`;
  }

  protected override async onRender() {
    const sharedData = getSharedData(this);
    const goToMembershipMain = () => {
      navigateTo(
        makePlusUrl(
          sharedData?.plus_crew?.domain_name,
          `/${ET.lang}/${this.data.creator.domain_name}/membership/main`,
        ),
        makeEmptyScreenNavigate,
      );
    };

    const goToMembershipProduct = () => {
      navigateTo(
        makePlusUrl(
          sharedData?.plus_crew?.domain_name,
          `/${ET.lang}/${this.data.creator.domain_name}/membership/products`,
        ),
        makeEmptyScreenNavigate,
      );
    };

    const goToCommissionMain = () => {
      navigateTo(
        makePlusUrl(
          sharedData?.plus_crew?.domain_name,
          `/${ET.lang}/${this.data.creator.domain_name}/membership/commission`,
        ),
        makeEmptyScreenNavigate,
      );
    };

    if (this.data.membership?.id) {
      try {
        const client = await getActivatedSdkWithEvent(this.data.membership.my.token);
        const widget = new CreatorFanServiceWidget({
          client,
          toggleSpinner: MembershipUtilS.toggleSpinner,
          isMobile: this.data.layout_options.is_mobile,
          projectId: this.data.membership.id,
          onError: this._membershipErrorEnable.onError,
          isApp: MShopUtilF.isApp(),
          onScrollLock: MShopUtilF.bodyFixed$,
          // 각 멤버십 상품 페이지로 이동
          onMembershipDimThumbnailClick: goToMembershipProduct,
          // 각 멤버십 상품 페이지로 이동
          onMembershipThumbnailClick: goToMembershipMain,
          // 각 멤버십 상품 페이지로 이동
          onMembershipJoinButtonClick: goToMembershipMain,
          // 각 리스트 페이지로 이동
          onCommissionThumbnailClick: goToCommissionMain,
          // 각 리스트 페이지로 이동
          onCommissionRequestButtonClick: goToCommissionMain,
        });
        const membership_wrap_elem = this.element().querySelector(`.${klass.membership_wrap}`) as HTMLElement;
        if (membership_wrap_elem) {
          widget.render(membership_wrap_elem);
        }
      } catch (e) {
        ConfigSentryF.error(e);
        await this._membershipErrorEnable.openWidgetRenderErrorModal();
      }
    }
  }
}

class StoreMainBody extends View<StoreMainPageData> {
  private fanLetterListView: FanLetterListView;
  private creatorMainProfileView: CreatorMainProfileView;

  constructor(data: StoreMainPageData) {
    super(data);
    const {
      fan_letters,
      creator,
      is_login,
      membership,
      adult_check_needed,
      layout_options: { is_mobile, user },
    } = this.data;
    this.fanLetterListView = new FanLetterListView({
      fan_letters: fan_letters.slice(0, is_mobile ? 10 : 4),
      creator,
      is_mobile,
      is_login,
      list_type: 'horizontal',
      item_mode: 'Large',
    });
    new FanLetterFormController(this.fanLetterListView.getFanLetterFormView());
    new FanLetterItemController(this.fanLetterListView.getFanLetterItemViews());

    const is_store_owner = creator.user_id == user?.id;
    this.creatorMainProfileView = new CreatorMainProfileView(
      {
        creator,
        is_login,
        is_mobile,
        is_store_owner,
        membership,
        color_set: 'black',
      },
      { adult_check_needed, is_mobile },
    );
  }

  private static _makeSectionTitle({
    title,
    is_mobile,
    uri,
  }: {
    title: string;
    is_mobile: boolean;
    uri?: string;
  }) {
    return new SectionTitle({
      title_props: {
        title,
        size: is_mobile ? 'medium' : 'xLarge',
      },
      plugins: uri
        ? [
            new SeeMoreLinkPlugin({
              see_more_link: uri,
              navigate: makeEmptyScreenNavigate(uri),
              size: is_mobile ? 'small' : 'xLarge',
            }),
          ]
        : [],
    });
  }

  protected override template(data: StoreMainPageData): Html {
    const { creator, adult_check_needed, products, photo_reviews, menus, popup, membership, fan_letters } =
      data;
    const { is_mobile } = data.layout_options;
    const sharedData = getSharedData(this);
    const product_more_label = ET('mps2::creator::see_more');
    const isAllEndedFuning = FundingHelper.isAllEndedFunding(this.data.fundings?.data || []);

    const creator_banner_img = () => {
      if (creator.is_adult && adult_check_needed) {
        if (is_mobile) {
          return CreatorConstantS.banner_img;
        } else {
          return CreatorConstantS.banner_img;
        }
      }
      if (is_mobile) {
        return creator.banner_img_mo || creator.banner_img || CreatorConstantS.banner_img;
      } else {
        return creator.banner_img || CreatorConstantS.banner_img;
      }
    };

    const makeUri = (param: string) => {
      return sharedData?.plus_crew && isAllCrewById(sharedData?.plus_crew.id)
        ? `/${ET.lang}/${creator.domain_name}/${param}`
        : makePlusUrl(sharedData?.plus_crew?.domain_name, `/${ET.lang}/${creator.domain_name}/${param}`);
    };
    return html`
      <div class="${klass.page}">
        <div class="${klass.banner}">
          <div class="${klass.gradient}">
            <img
              src="${getResizedUrl({
                url: creator_banner_img(),
                width: is_mobile ? 750 * 2 : 1440 * 2,
                format: 'webp',
                quality: 80,
              })}"
              alt=""
            />
          </div>
          <div class="${klass.creator_profile}">${this.creatorMainProfileView}</div>
        </div>
        ${is_mobile
          ? html`<div class="${klass.sub_header}">
              ${new CreatorShopSubHeader({
                menus,
                plus_domain: sharedData?.plus_crew?.domain_name,
                focus_on_select: false,
              })}
            </div>`
          : ''}
        <div class="${klass.store_main_wrap}">
          <!-- 펀딩 -->
          ${doIf(
            () => html`
              <section class="${klass.section_wrapper}">
                <div class="${klass.title}">
                  ${StoreMainBody._makeSectionTitle({
                    title: ET('creator_shop::section_title::fundings'),
                    is_mobile,
                    uri:
                      this.data.fundings?.data && this.data.fundings?.data.length > 2
                        ? makeUri('fundings')
                        : undefined,
                  })}
                </div>

                <div class="${klass.fundings}">
                  ${pipe(
                    this.data.fundings?.data || [],
                    take(2),
                    map(
                      (funding) =>
                        new FundingCard({
                          product_id: funding.productId,
                          domain_name: funding.domainName,
                          name: funding.name,
                          description: funding.description,
                          goal_amount: funding.goalAmount,
                          total_amount: funding.totalAmount,
                          thumbnail_info: funding.thumbnailInfo,
                          sell_start_at: funding.sellStartAt,
                          sell_end_at: funding.sellEndAt,
                          is_quantity_public: funding.isQuantityPublic,
                          is_public: funding.isPublic,
                          order_count: funding.orderCount,
                          quantity: funding.quantity,
                          out_of_stock: funding.outOfStock,
                          server_timestamp: this.data.server_timestamp,
                          is_mobile,
                        }),
                    ),
                    toArray,
                  )}
                </div>
              </section>
            `,
            Boolean(this.data.fundings?.data.length),
          )}
          ${this.data.membership?.id &&
          (membership?.membership_plan_existed || membership?.membership_commission_existed) &&
          !(creator.is_adult && adult_check_needed)
            ? // TODO: @blan22
              html`
                <section class="${klass.section_wrapper} ${isAllEndedFuning ? klass.fundings_all_ended : ''}">
                  ${new MembershipView({
                    ...this.data,
                    // TODO 섹션타이틀 까지 가져오는 이유?
                    // title: StoreMainBody._makeSectionTitle({
                    //   title: '크리에이터 팬 서비스',
                    //   is_mobile,
                    //   uri: makeUri('membership/main'),
                    // }),
                  })}
                </section>
              `
            : ''}

          <div class="${klass.section_wrapper} ${isAllEndedFuning ? klass.fundings_all_ended : ''}">
            <div class="${klass.title}">
              ${StoreMainBody._makeSectionTitle({
                title: ET('creator_shop::section_title::products'),
                is_mobile,
                uri: makeUri('products'),
              })}
            </div>
            ${new ProductCardListView(
              { products: products.slice(0, 20) },
              {
                resize_width: 600,
                is_lazy: true,
                container_klass: klass.product_cards_container,
                size: is_mobile ? ProductCardSize.small : ProductCardSize.large,
              },
            ).setEmptyListView(new CreatorShopProductListEmptyScreen())}
            ${products.length > 10
              ? html`
                  <div class="${klass.btn_more_area}">
                    ${new ButtonAction({
                      label: product_more_label,
                      klass: klass.btn_more,
                      fit: true,
                      href: makeUri('products'),
                      navigate: makeEmptyScreenNavigate(makeUri('products')),
                      size: is_mobile ? 'regular' : 'large',
                    })}
                  </div>
                `
              : ''}
          </div>
          <div class="${klass.section_wrapper} ">
            <div class="${klass.title}">
              ${StoreMainBody._makeSectionTitle({
                title: ET('creator_shop::fan_letters::title'),
                is_mobile,
                uri: makeUri('fan-letter'),
              })}
            </div>
            <div class="${klass.fan_letter_list_view}">
              ${this.fanLetterListView}${is_mobile && fan_letters.length > 0
                ? html`
                    <div class="${klass.btn_more_area}">
                      ${new ButtonAction({
                        label: product_more_label,
                        klass: klass.btn_more,
                        fit: true,
                        href: makeUri('fan-letter'),
                        navigate: makeEmptyScreenNavigate(makeUri('fan-letter')),
                        size: is_mobile ? 'regular' : 'large',
                      })}
                    </div>
                  `
                : ''}
            </div>
          </div>
          ${photo_reviews.length
            ? html`
                <div class="${klass.section_wrapper}">
                  <div class="${klass.title}">
                    ${StoreMainBody._makeSectionTitle({
                      title: ET('creator_shop::section_title::reviews'),
                      is_mobile,
                      uri: makeUri('review'),
                    })}
                  </div>
                  ${new PhotoReviewList(photo_reviews.slice(0, is_mobile ? 10 : 5))}
                  ${is_mobile && photo_reviews.length > 0
                    ? html`
                        <div class="${klass.btn_more_area}">
                          ${new ButtonAction({
                            label: product_more_label,
                            klass: klass.btn_more,
                            fit: true,
                            href: makeUri('review'),
                            navigate: makeEmptyScreenNavigate(makeUri('review')),
                            size: is_mobile ? 'regular' : 'large',
                          })}
                        </div>
                      `
                    : ''}
                </div>
              `
            : ''}
          ${popup.popups.length > 0 ? new Popup(popup) : ''}
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.addEventListener(BecomeFanButtonEvent, (e) => {
      if (e.detail.is_liked) {
        this.creatorMainProfileView.getFanCountView().plusCount();
      } else {
        this.creatorMainProfileView.getFanCountView().minusCount();
      }
      BecomeFanButtonController.postLike(e.detail, () => {
        this.creatorMainProfileView.getBecomeFanButtonView().toggleLiked();
      });
    });

    const is_mobile = !!getSharedData(this)?.is_mobile;
    const sub_header_view = this.subView(CreatorShopSubHeader);

    if (sub_header_view && is_mobile) {
      applySubHeaderHeadroom(HeaderCreatorShop, sub_header_view, { fixed: true });
    }
  }
}

export class StoreMainPage extends Page<StoreMainPageData> {
  protected override template(data: StoreMainPageData): Html {
    const sharedData = getSharedData(this);
    const { layout_options, recent_product } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new StoreMainBody(data),
            footer_data: { ...footer_links, is_mobile: layout_options.is_mobile, lang: ET.lang },
          },
          {
            full_width: true,
            has_bottom_tab_bar: !sharedData?.webviewapp,
            selected_bottom_tab_idx: -1,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(recent_product),
              share: {
                title: sanitizer.unescapeEntities(data.creator.name) || 'Creator',
                description: ET('mps2::share::store_main_description', {
                  creator_name: data.creator.name || 'creator',
                }),
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
